import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { MessagesSquare, ArrowRight, Loader2, Clock, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { apiErrorToast } from "../lib/utils";
import { TARGET_ROLE_GROUPS } from "../constants/targetRoles";

export default function MockInterview() {
  const navigate = useNavigate();
  const [level, setLevel] = useState("L1");
  const [role, setRole] = useState("Software Engineer");
  const [resumes, setResumes] = useState([]);
  const [resumeId, setResumeId] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [a, b] = await Promise.all([api.get("/resume/list"), api.get("/interview/list/all")]);
      setResumes(a.data.items || []);
      setHistory(b.data.items || []);
      if ((a.data.items || []).length) setResumeId(a.data.items[0].resume_id);
    })();
  }, []);

  const start = async () => {
    setLoading(true);
    try {
      const r = await api.post("/interview/start", { level, role, resume_id: resumeId || null });
      toast.success(`${level} interview ready · ${r.data.questions.length} questions`);
      navigate(`/app/interview/${r.data.id}`);
    } catch (e) {
      apiErrorToast(e, "Could not start interview");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="interview-home">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Mock Interview</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Practice the round you&apos;re about to face.</h1>
        <p className="text-zinc-600 mt-2 text-sm max-w-2xl">L1 = HR screening (15 Q). L2 = Technical (20 Q tailored to your resume). L3 = Managerial / case-study (12 Q senior-level).</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-flat p-6 lg:col-span-1">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Configure</div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <button data-testid="level-L1" onClick={()=>setLevel("L1")} className={`h-11 rounded-md border text-sm font-semibold ${level==='L1'?'bg-[#0A0A0A] text-white border-black':'border-zinc-300 hover:border-zinc-900'}`}>L1 · HR</button>
            <button data-testid="level-L2" onClick={()=>setLevel("L2")} className={`h-11 rounded-md border text-sm font-semibold ${level==='L2'?'bg-[#0A0A0A] text-white border-black':'border-zinc-300 hover:border-zinc-900'}`}>L2 · Tech</button>
            <button data-testid="level-L3" onClick={()=>setLevel("L3")} className={`h-11 rounded-md border text-sm font-semibold ${level==='L3'?'bg-[#0A0A0A] text-white border-black':'border-zinc-300 hover:border-zinc-900'}`}>L3 · Manager</button>
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Role</label>
            <select data-testid="interview-role" className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md bg-white" value={role} onChange={(e)=>setRole(e.target.value)}>
              {TARGET_ROLE_GROUPS.map(g => (
                <optgroup key={g.label} label={g.label}>
                  {g.roles.map(r => <option key={r} value={r}>{r}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Resume (optional)</label>
            <select data-testid="interview-resume" className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md bg-white" value={resumeId} onChange={(e)=>setResumeId(e.target.value)}>
              <option value="">None — use role only</option>
              {resumes.map(r => <option key={r.id} value={r.resume_id}>{r.target_role} · {new Date(r.created_at).toLocaleDateString()}</option>)}
            </select>
          </div>
          <button data-testid="interview-start-btn" onClick={start} disabled={loading} className="mt-5 w-full h-11 rounded-md bg-[#0E7C66] text-white font-semibold hover:bg-[#0A5E4D] transition flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <>Start interview <ArrowRight size={14} /></>}
          </button>
          <p className="text-xs text-zinc-500 mt-3 flex items-center gap-1"><Clock size={12} /> ~10-15 min recommended</p>
        </div>

        <div className="card-flat p-6 lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Past sessions</div>
          {history.length === 0 ? (
            <div className="text-center py-12">
              <div className="h-12 w-12 mx-auto rounded-full bg-zinc-100 flex items-center justify-center"><MessagesSquare size={20} className="text-zinc-500" /></div>
              <div className="font-display font-bold mt-4 text-lg">No sessions yet</div>
              <p className="text-sm text-zinc-500 max-w-sm mx-auto mt-1">Start your first mock interview using the panel on the left.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.id} className="flex items-center justify-between p-4 border border-zinc-200 rounded-lg" data-testid={`interview-history-${h.id}`}>
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-zinc-100">{h.level}</span> {h.role}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{new Date(h.created_at).toLocaleString()} · {h.status}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {h.status === 'completed' && <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Score</div>
                      <div className="font-mono font-bold">{h.overall_score}/100</div>
                    </div>}
                    <button onClick={()=>navigate(h.status==='completed' ? `/app/interview/${h.id}/review` : `/app/interview/${h.id}`)} className="text-sm px-3 py-1.5 rounded-md border border-zinc-300 hover:border-zinc-900 font-semibold flex items-center gap-1" data-testid={`open-interview-${h.id}`}>
                      {h.status === 'completed' ? <>Review <BadgeCheck size={12} /></> : <>Continue <ArrowRight size={12} /></>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
